import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography, CircularProgress, Select, MenuItem, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from "react";
import { walletApi } from "../../api/api";

const WalletPage = () => {
  const [loading, setLoading] = useState(true);
  const [walletData, setWalletData] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const navigate = useNavigate();

  // Filter rows based on search and status
  const filteredRows = walletData.filter((row) => {
    const matchesSearch =
      row.Username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.Amount?.toString().includes(searchTerm);
    const matchesStatus = statusFilter ? row.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (Status) => {
    switch (Status) {
      case "Inprogress": return "orange";
      case "Rejected": return "red";
      case "Completed": return "green";
      default: return "#000000";
    }
  };

  useEffect(() => {
    const fetchWalletData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await walletApi.getWallet();
        if (Array.isArray(response.data)) {
          setWalletData(response.data);
        } else if (response.status === 404) {
          setError('No Transactions Found');
        } else {
          setWalletData([]);
          setError('API response is not in expected format');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setWalletData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchWalletData();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return '';
    return date.toLocaleDateString();
  };

  const capitalizeName = (name) => {
    if (!name) return '';
    return name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "Username", headerName: "User Name", width: 150 },
    {
      field: "Amount", headerName: "Wallet Balance", width: 200,
      renderCell: (params) => <span>{capitalizeName(params.value)}</span>
    },
    { field: "Requested Amount", headerName: "Withdraw Amount", width: 200 },
    { field: "Type", headerName: "Type", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 220,
      renderCell: (params) => {
        const statusColor = getStatusColor(params.value);
        return (
          <div
            style={{ color: statusColor, fontWeight: "bold", cursor: "pointer" }}
            onClick={() =>
              navigate(`/wallet/statusPage/${params.row.status}`, {
                state: {
                  id: params.row.id,
                  userId: params.row.userId,
                  Amount: params.row.Amount,
                  Type: params.row.Type,
                  status: params.row.status,
                  InvestorName: params.row.Username,
                },
              })
            }
          >
            {params.value}
          </div>
        );
      },
    },
    {
      field: "createdAt", headerName: "Created At", width: 200,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: "updateAction",
      headerName: "Update",
      width: 120,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() =>
            navigate(`/wallet/statusPage/${params.row.status}`, {
              state: {
                id: params.row.id,
                userId: params.row.userId,
                Amount: params.row.Amount,
                Type: params.row.Type,
                status: params.row.status,
                InvestorName: params.row.Username,
              },
            })
          }
        >
          Update
        </Button>
      )
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh", 
        marginTop:3
      }}
    >
       <Typography sx={{ color: "#13216e", fontWeight: 600, fontSize: "20px",marginBottom:2 }}>
              Wallet
            </Typography>
      {/* Header Section */}
      <Box sx={{ border: "2px solid lightgray", borderRadius: "5px", p: 2 }}>
        <Grid container spacing={2} alignItems="center">
         

          {/* Search */}
          <Grid item xs={12} sm={4}>
            <input
              type="text"
              placeholder="Search by name or amount"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
                width: "100%"
              }}
            />
          </Grid>

          {/* Status Filter */}
          <Grid item xs={12} sm={2}>
            <Select
              fullWidth
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Inprogress">InProgress</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </Select>
          </Grid>

          {/* Count */}
          <Grid item xs={12} sm={2} sx={{ textAlign: "right" }}>
            <Typography style={{ color: "#4e0dff", fontSize: "16px" }}>
              Total Wallets: {filteredRows.length}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* DataGrid Section */}
      <Box sx={{ flexGrow: 1, mt: 2 }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.userId}
          pageSize={5}
          rowsPerPageOptions={[5, 10, 25]}
          disableSelectionOnClick
           sx={{
              height: 650,
              border: '1px solid #ccc',
              borderRadius: 2,
              fontSize: '0.9rem',
              backgroundColor: '#ffffff',
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f0f0f0ff',
              },
              '& .MuiDataGrid-cell': {
                color: '#333',
                borderBottom: '1px solid #ddd',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f0f0f0ff',
                color: 'black',
                fontWeight: 'bold',
              },
              '& .MuiTablePagination-root': {
                fontSize: '0.875rem',
              },
            }}
        />
      </Box>
    </Box>
  );
};

export default WalletPage;
